import React, { useContext, useMemo, useState } from 'react'
import { ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import { SwapPoolTabs, Tabs } from 'components/NavigationTabs'
import PositionList from 'components/PositionList'
import { useActiveWeb3React } from 'hooks'
import { useV3Positions } from 'hooks/useV3Positions'
import { Inbox } from 'react-feather'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { usePrivy } from '@privy-io/react-auth'
import { ThemeContext } from 'styled-components'
import { HideSmall, TYPE } from 'theme'
import {
  ButtonRow,
  LoadingContainer,
  LoadingRows,
  MainContentWrapper,
  NoLiquidity,
  PageWrapper,
  ResponsiveButtonPrimary,
  TitleRow,
} from './styleds'
import IntroCard from 'components/IntroCard'
import { Helmet } from 'react-helmet'
import { StyledTabButton } from 'pages/FarmPool'
import PoolV2 from './v2'
import Modal from 'components/Modal'
import ClaimAll from './ClaimAll'
import ClaimIncentives from './ClaimIncentives'
import { useRewards } from 'hooks/merkle/useCampaigns'

function useIsPoolV2() {
  const location = useLocation()

  return useMemo(() => {
    return location.pathname.startsWith('/pool/v2')
  }, [location.pathname])
}

export default function Pool() {
  const { account } = useActiveWeb3React()
  const { rewards } = useRewards()

  const history = useHistory()
  const isV2 = useIsPoolV2()
  const [claimModalOpen, setClaimModalOpen] = useState(false)
  const [claimIncentivesModalOpen, setClaimIncentivesModalOpen] = useState(false)

  const { login } = usePrivy()
  const theme = useContext(ThemeContext)

  const { positions, loading: positionsLoading } = useV3Positions(account)

  return (
    <>
      <Helmet>
        <title>My Pool Positions | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Manage your liquidity positions on HyperSwap Exchange with real-time data. Built on Hyperliquid's EVM for fast and reliable DeFi operations."
        />
        <meta
          name="keywords"
          content="HyperSwap pool positions, Hyperliquid blockchain, DeFi tools, liquidity positions, manage pools, Hyperliquid EVM"
        />
        <meta property="og:title" content="My Pool Positions | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Track and optimize your liquidity pool positions on HyperSwap Exchange, built on Hyperliquid."
        />
        <meta property="og:url" content={`${window.location.origin}/#/pool`} />
      </Helmet>

      <Modal isOpen={claimModalOpen} onDismiss={() => setClaimModalOpen(false)}>
        <ClaimAll positions={positions} onDismiss={() => setClaimModalOpen(false)} />
      </Modal>
      <Modal isOpen={claimIncentivesModalOpen} onDismiss={() => setClaimIncentivesModalOpen(false)}>
        <ClaimIncentives rewards={rewards?.rewards} onDismiss={() => setClaimIncentivesModalOpen(false)} />
      </Modal>
      <PageWrapper>
        <IntroCard variant={1} Key="pool">
          <h1>Your Positions Overview</h1>
          <p>Manage your liquidity pools and view your positions.</p>
        </IntroCard>
        <SwapPoolTabs active={'pool'} />
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow style={{ marginTop: '1rem' }} padding={'0'}>
              <HideSmall>
                <TYPE.mediumHeader>Pools Overview</TYPE.mediumHeader>
              </HideSmall>
              <ButtonRow>
                <ResponsiveButtonPrimary id="join-pool-button" as={Link} to="/add/HYPE">
                  + New Position
                </ResponsiveButtonPrimary>
              </ButtonRow>
            </TitleRow>
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-start' }}>
              <Tabs style={{ gap: '6px' }}>
                <StyledTabButton
                  isActive={isV2}
                  onClick={() => history.push('/pool/v2')}
                  style={{ background: !isV2 ? '#1F1C29' : '' }}
                >
                  Standard AMM
                </StyledTabButton>
                <StyledTabButton
                  isActive={!isV2}
                  onClick={() => history.push('/pool')}
                  style={{ background: isV2 ? '#1F1C29' : '' }}
                >
                  Concentrated Liquidity
                </StyledTabButton>
              </Tabs>
            </div>
            {isV2 ? (
              <>
                <PoolV2 />
              </>
            ) : (
              <>
                <MainContentWrapper>
                  {positionsLoading ? (
                    <LoadingContainer>
                      <LoadingRows>
                        {Array.from({ length: 12 }).map((_, i) => (
                          <div key={i} />
                        ))}
                      </LoadingRows>
                    </LoadingContainer>
                  ) : positions && positions.length > 0 ? (
                    <PositionList
                      positions={positions}
                      rewards={rewards?.rewards}
                      setClaimModalOpen={setClaimModalOpen}
                      setClaimIncentivesModalOpen={setClaimIncentivesModalOpen}
                    />
                  ) : (
                    <LoadingContainer>
                      <NoLiquidity>
                        <TYPE.mediumHeader color={theme.text3} textAlign="center">
                          <Inbox size={48} strokeWidth={1} style={{ marginBottom: '.5rem' }} />
                          <div>Your V3 liquidity positions will appear here.</div>
                        </TYPE.mediumHeader>
                        {!account ? (
                          <ButtonPrimary
                            style={{ marginTop: '2em', padding: '8px 16px' }}
                            onClick={() =>
                              login({
                                loginMethods: ['wallet'],
                                walletChainType: 'ethereum-only',
                                disableSignup: false,
                              })
                            }
                          >
                            Connect a wallet
                          </ButtonPrimary>
                        ) : (
                          <>
                            {/*  <ButtonGray
                            as={Link}
                            to="/migrate/v2"
                            id="import-pool-link"
                            style={{
                              marginTop: '2em',
                              padding: '8px 16px',
                              borderRadius: '12px',
                              width: 'fit-content',
                            }}
                          >
                            {t('Migrate V2 liquidity')}?&nbsp;&nbsp;
                            <Download size={16} />
                          </ButtonGray> */}
                          </>
                        )}
                      </NoLiquidity>
                    </LoadingContainer>
                  )}
                </MainContentWrapper>
              </>
            )}
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
    </>
  )
}
